"use client";

import React, { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import ReactPlayer from "react-player/youtube";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import Image from "next/image";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CancelIcon from "@mui/icons-material/Cancel";
import clsx from "clsx";
import { Pagination as PaginationComponent } from "@/components/Pagination";
import { usePathname } from "next/navigation";

dayjs.extend(relativeTime);

interface VideoProps {
  url: string;
  title: string;
  channelTitle: string;
  thumbnail: string;
  publishedAt: string;
  viewCount: number;
}

interface YouTubePlaylistProps {
  videos: Array<VideoProps>;
  videosTemplate?: number;
  totalVideosCount?: number;
  pageParam?: number;
}

const YouTubePlaylist: React.FC<YouTubePlaylistProps> = ({
  videos,
  videosTemplate = 4,
  totalVideosCount,
  pageParam = 1,
}) => {
  const videoListRef = useRef<HTMLDivElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [playingIndex, setPlayingIndex] = useState<number | null>(null);
  const [currentVideo, setCurrentVideo] = useState<string>("");
  const [hoveredVideo, setHoveredVideo] = useState<string | null>(null);

  const currentPage = pageParam;
  const pathname = usePathname();

  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [isModalOpen]);

  const [isFirstRender, setIsFirstRender] = useState(true);
  useEffect(() => {
    if (isFirstRender) {
      setIsFirstRender(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (isFirstRender) return <div />;

  return (
    <div ref={videoListRef} className="grid-container mb-16">
      <div className="col-start-1 col-span-4 lg:col-start-2 lg:col-span-14 2xl:col-start-4 2xl:col-span-26">
        {videosTemplate == 4 && (
          <Swiper
            className="lg:!hidden"
            spaceBetween={10}
            slidesPerView={1.25}
            centeredSlides={true}
            loop={false}
            pagination={{ clickable: true }}
            modules={[Pagination]}
            onTransitionStart={() => {
              setPlayingIndex(null);
            }}
            grabCursor
          >
            {videos?.map((video: VideoProps, index: number) => (
              <SwiperSlide key={`${video.url}-${index}`}>
                <div className="flex flex-col">
                  <div
                    className="relative rounded-[20px] overflow-hidden aspect-[1.8] w-full h-full max-h-[200px]"
                    onClick={() => {
                      setPlayingIndex((prevIndex) =>
                        prevIndex === index ? null : index
                      );
                    }}
                    onTouchStart={(e) => e.stopPropagation()}
                    onTouchMove={(e) => e.stopPropagation()}
                  >
                    <div
                      className={clsx({
                        "pointer-events-none": playingIndex !== index,
                        "pointer-events-auto": playingIndex === index,
                      })}
                    >
                      <ReactPlayer
                        thumbnail={video.thumbnail}
                        className="absolute inset-0 rounded-[20px]"
                        url={video.url}
                        width="100%"
                        height="100%"
                        playing={playingIndex === index}
                        controls
                        stopOnUnmount
                      />
                    </div>
                  </div>
                  {/* Description */}
                  <div className="mt-2 mb-4 flex flex-col gap-3 text-white">
                    <p className="font-poppins text-[16px] font-normal leading-[24px]">
                      {video.title}
                    </p>
                    <p className="font-poppins text-xs font-normal leading-[16px] tracking-[0.36px] text-tm-c-gray-500">
                      {video.channelTitle}
                    </p>
                    <p className="font-poppins text-xs font-light leading-[16px] tracking-[0.36px] text-tm-c-gray-500">
                      {video.viewCount} views •{" "}
                      {dayjs(video.publishedAt).fromNow()}
                    </p>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        )}
        {videosTemplate == 16 && (
          <div className="md:grid md:grid-cols-2 md:row-span-1 lg:hidden flex flex-col gap-[15px]">
            {videos.map((video: VideoProps, index: number) => (
              <div
                key={`${video.url}-${index}`}
                className="flex flex-col gap-2"
              >
                <div className="relative rounded-[20px] overflow-hidden aspect-[1.8] w-full h-full max-h-[200px]">
                  <ReactPlayer
                    thumbnail={video.thumbnail}
                    className="absolute inset-0 rounded-[20px]"
                    url={video.url}
                    width="100%"
                    height="100%"
                    playing={playingIndex === index}
                    controls
                    stopOnUnmount
                  />

                  {/* Overlay gradient */}
                  {playingIndex !== index && (
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-black/20 pointer-events-none" />
                  )}
                </div>
                {/* Description */}
                <div className="flex flex-col gap-3 text-white">
                  <p className="font-poppins text-[16px] font-normal leading-[24px]">
                    {video.title}
                  </p>
                  <p className="font-poppins text-xs font-light leading-[16px] tracking-[0.36px] text-tm-c-gray-500">
                    {video.viewCount} views •{" "}
                    {dayjs(video.publishedAt).fromNow()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
        <div>
          <div className="hidden lg:grid lg:grid-cols-4 lg:row-span-1 lg:gap-y-10 lg:gap-x-3">
            {videos?.map((video: VideoProps, index: number) => (
              <div
                key={`${video.url}-${index}`}
                className="relative flex flex-col"
              >
                {/* Video Thumbnail */}
                <div
                  onClick={(e) => {
                    setIsModalOpen(true);
                    setCurrentVideo(video.url);
                  }}
                  onMouseEnter={() => setHoveredVideo(video.url)}
                  onMouseLeave={() => setHoveredVideo(null)}
                  className="relative cursor-pointer aspect-[1.6] w-full h-full max-h-[200px] rounded-[20px] overflow-hidden"
                >
                  {hoveredVideo === video.url ? (
                    <ReactPlayer
                      url={video.url}
                      width="100%"
                      height="100%"
                      muted
                      controls={false}
                      playing={hoveredVideo === video.url}
                    />
                  ) : (
                    <>
                      {video.thumbnail ? (
                        <Image
                          src={video.thumbnail}
                          fill
                          sizes="100vw"
                          className="object-cover"
                          alt="youtube thumbnail"
                        />
                      ) : (
                        <div className="w-full h-full bg-[#272727]" />
                      )}
                      <PlayArrowIcon
                        className="absolute z-[1] inset-x-0 inset-y-0 m-auto text-center"
                        style={{
                          fontSize: 60,
                        }}
                      />
                    </>
                  )}

                  {/* Overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-black/20 rounded-[20px]"></div>
                </div>
                {/* Description */}
                <div className="flex flex-col gap-3 mt-[10px] px-5">
                  <p className="max-w-[250px] font-poppins text-[16px] font-light leading-[24px] text-white">
                    {video.title}
                  </p>
                  <p className="font-poppins text-xs font-light leading-[16px] tracking-[0.36px] text-tm-c-gray-500">
                    {video.channelTitle}
                  </p>
                  <p className="font-poppins text-xs font-light leading-[16px] tracking-[0.36px] text-tm-c-gray-500">
                    {video.viewCount} views •{" "}
                    {dayjs(video.publishedAt).fromNow()}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {videosTemplate == 16 && totalVideosCount && (
            <PaginationComponent
              currentPage={currentPage}
              totalPages={Math.ceil(totalVideosCount / videosTemplate)}
              basePath={pathname}
            />
          )}
          {/* Portal to render the video dialog */}
          {currentVideo && isModalOpen && (
            <>
              <div
                className="fixed inset-0 bg-black bg-opacity-90 z-30"
                onClick={() => {
                  setIsModalOpen(false);
                }}
              />
              <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[80vw] h-auto z-30 bg-black bg-opacity-80">
                <CancelIcon
                  style={{ fontSize: 36 }}
                  className="absolute top-[-30px] right-[-30px] z-20 text-white text-opacity-80 cursor-pointer transition-colors duration-300"
                  onClick={() => {
                    setIsModalOpen(false);
                    setCurrentVideo("");
                  }}
                />
                <div className="relative pt-[56.25%] rounded-[20px] overflow-hidden">
                  <ReactPlayer
                    url={currentVideo}
                    width="100%"
                    height="100%"
                    className="absolute top-0 left-0 w-full h-full"
                    playing={isModalOpen}
                    controls
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default YouTubePlaylist;
